import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaYoutube,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
} from "react-icons/fa";

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const socialLinks = [
    { icon: <FaFacebook />, url: "https://www.facebook.com/worganaturals", name: "Facebook" },
    { icon: <FaTwitter />, url: "https://x.com/worganaturals", name: "Twitter" },
    { icon: <FaInstagram />, url: "https://www.instagram.com/worganaturals", name: "Instagram" },
    { icon: <FaLinkedin />, url: "https://www.linkedin.com/company/worga-naturals-pvt-ltd/?originalSubdomain=lk", name: "LinkedIn" },
    { icon: <FaYoutube />, url: "https://www.youtube.com/channel/UCEi75OEQ9L_LV3NrYbM_Rbw", name: "YouTube" },
  ];

  // ✅ Updated certifications with actual logo images
  const certifications = [
    {
      name: "BRC",
      logo: "/images/certifications/BRC.webp",
      alt: "BRC Food Certificated",
    },
    {
      name: "EU Organic",
      logo: "/images/certifications/EU.webp",
      alt: "EU Organic Certification",
    },
    {
      name: "USDA",
      logo: "/images/certifications/USDA.webp",
      alt: "USDA Organic",
    },
    {
      name: "JAS",
      logo: "/images/certifications/JAS.webp",
      alt: "JAS Organic Certification",
    },
    {
      name: "Naturland",
      logo: "/images/certifications/Naturland.webp",
      alt: "Naturland Certification",
    },
    {
      name: "Fair Trade",
      logo: "/images/certifications/FairTrade.webp",
      alt: "Fair Trade Certified",
    },
    {
      name: "FDA",
      logo: "/images/certifications/FDA.webp",
      alt: "FDA Approved",
    },
  ];

  const footerLinks = {
    quickLinks: [
      { name: t("footer.home") || "Home", path: "/" },
      { name: t("footer.aboutUs") || "About Us", path: "/about/who-we-are" },
      {
        name: t("footer.products") || "Products",
        path: "/products/king-coconut-water",
      },
      {
        name: t("footer.certifications") || "Certifications",
        path: "/quality-sustainability",
      },
      { name: t("footer.news") || "News", path: "/news" },
      { name: t("footer.contact") || "Contact", path: "/contact" },
    ],
    products: [
      {
        name: t("footer.kingCoconutWater") || "King Coconut Water",
        path: "/products/king-coconut-water",
      },
      {
        name: t("footer.cannedFruit") || "Canned/Bottle Fruit",
        path: "/products/canned-bottle-fruit",
      },
      {
        name: t("footer.dehydratedFruits") || "Dehydrated Fruits",
        path: "/products/dehydrated-fruits",
      },
      {
        name: t("footer.fruitJuice") || "Fruit Juice & Pulp",
        path: "/products/fruit-juice-pulp",
      },
      {
        name: t("footer.fruitPowder") || "Fruit Powder",
        path: "/products/fruit-powder",
      },
      { name: t("footer.spices") || "Spices", path: "/products/spices" },
    ],
  };

  return (
    <footer className="bg-dark-900 text-gray-300">
      {/* Main Footer */}
      <div className="container mx-auto px-8 lg:px-16 xl:px-20 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link
              to="/"
              onClick={scrollToTop}
              className="flex items-center gap-3 mb-6"
            >
              <img
                src="/images/logo.png"
                alt="Worga Naturals Logo"
                className="h-14 w-auto"
              />
            </Link>
            <h3 className="text-xl font-bold text-white mb-2">
              Worga Naturals
            </h3>
            <p className="text-sm text-primary-400 mb-4">
              {t("footer.tagline") || "Organic Excellence"}
            </p>
            <p className="text-gray-400 mb-6 leading-relaxed text-sm">
              {t("footer.description") ||
                "Leading organic food exporter from Sri Lanka, delivering premium tropical fruits, coconut products, and authentic spices to the world."}
            </p>

            {/* ✅ Certifications with Logos */}
            <div className="mb-6">
              <h4 className="text-xs font-semibold text-white mb-2">
                {t("footer.certificationsTitle") || "Certifications"}
              </h4>
              <div className="flex flex-wrap gap-1">
                {certifications.map((cert, index) => (
                  <div
                    key={index}
                    className="rounded p-0.5  hover:border-primary-500 transition-all duration-300 hover:scale-110 group w-9 h-9"
                    title={cert.alt}
                  >
                    <img
                      src={cert.logo}
                      alt={cert.alt}
                      className="w-full h-full object-contain opacity-80 group-hover:opacity-100 transition-opacity"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold text-white mb-6">
              {t("footer.quickLinks") || "Quick Links"}
            </h4>
            <ul className="space-y-3">
              {footerLinks.quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    onClick={scrollToTop}
                    className="text-gray-400 hover:text-primary-300 transition-colors duration-300 flex items-center gap-2 group text-sm"
                  >
                    <span className="w-0 group-hover:w-2 h-0.5 bg-primary-400 transition-all duration-300"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Products Links */}
          <div>
            <h4 className="text-lg font-bold text-white mb-6">
              {t("footer.ourProducts") || "Our Products"}
            </h4>
            <ul className="space-y-3">
              {footerLinks.products.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    onClick={scrollToTop}
                    className="text-gray-400 hover:text-primary-300 transition-colors duration-300 flex items-center gap-2 group text-sm"
                  >
                    <span className="w-0 group-hover:w-2 h-0.5 bg-primary-400 transition-all duration-300"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold text-white mb-6">
              {t("footer.contactInfo") || "Contact Info"}
            </h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <FaMapMarkerAlt className="text-primary-500 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-white mb-1">
                    {t("footer.address") || "Address"}
                  </p>
                  <p className="text-sm text-gray-400">
                    No. 152/G, Meepavita,
                    <br />
                    Udamapitigama,
                    <br />
                    Dompe ( 11680 )<br />
                    Sri Lanka
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <FaPhone className="text-primary-500 mt-1 flex-shrink-0" />
                <div>
                  <a
                    href="tel:+94773788508"
                    className="block text-sm text-gray-400 hover:text-primary-300 transition-colors duration-300"
                  >
                    +94 77 378 8508
                  </a>
                  <a
                    href="tel:+94713992673"
                    className="block text-sm text-gray-400 hover:text-primary-300 transition-colors duration-300"
                  >
                    +94 71 399 2673
                  </a>
                </div>
              </div>

              <a
                href="mailto:info@worganaturals.com"
                className="flex items-start gap-3 text-gray-400 hover:text-primary-300 transition-colors duration-300 group"
              >
                <FaEnvelope className="text-primary-500 mt-1 flex-shrink-0 group-hover:scale-110 transition-transform" />
                <span className="text-sm">info@worganaturals.com</span>
              </a>

              {/* Social Links */}
              <div>
                <h4 className="text-sm font-semibold text-white mb-3">
                  {t("footer.followUs") || "Follow Us"}
                </h4>
                <div className="flex gap-3">
                  {socialLinks.map((social, index) => (
                    <a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-9 h-9 bg-dark-800 rounded-full flex items-center justify-center text-gray-400 hover:bg-primary-600 hover:text-white transition-all duration-300 hover:scale-110"
                      aria-label={social.name}
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-dark-800">
        <div className="container mx-auto px-8 lg:px-16 xl:px-20 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {currentYear} Worga Naturals (Pvt) Ltd. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <Link
                to="/privacy"
                onClick={scrollToTop}
                className="text-gray-400 hover:text-primary-300 transition-colors duration-300"
              >
                {t("footer.privacy") || "Privacy Policy"}
              </Link>
              <Link
                to="/terms"
                onClick={scrollToTop}
                className="text-gray-400 hover:text-primary-300 transition-colors duration-300"
              >
                {t("footer.terms") || "Terms of Service"}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
